import {
  ChangeDetectorRef,
  Component,
  input,
  model,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { Observable } from 'rxjs';

import { debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  ConceptTopicItem,
  ConfigurationService,
  QueryTopicItem,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { IndustryPackService } from 'app/providers/semantria-api/industry-pack.service';
import { LanguageTemplateService } from 'app/providers/semantria-api/language-template.service';

import { Document } from 'app/providers/storage-api/document.service';
import { Report, ReportService } from 'app/providers/storage-api/report.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';
import { DocumentViewOptions } from '../../document-view/document-view.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-topic-editor',
  templateUrl: './topic-editor.component.html',
  styleUrls: ['./topic-editor.component.scss'],
  standalone: false,
})
export class TopicEditorComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly options = input<DocumentViewOptions>();
  readonly document = input<Document>();
  readonly configId = input<string>();
  readonly analysisConfigId = input<string>();
  readonly topicToEdit = model<any>();
  readonly isLoading = input<boolean>(false);

  readonly cancel = output<void>();
  readonly delete = output<any>();
  readonly save = output<any>();

  @ViewChild('titleAutocomplete') titleAutocomplete: MatAutocomplete;
  autocompleteResult: Observable<{ title: string; disabled: boolean }[]>;

  topic: any = {
    strengthScore: 0.0,
    sentimentScore: 0.0,
    sentimentPolarity: 'neutral',
    title: '',
    type: 'manual',
  };
  comment: string;
  topicShouldHit = true;

  titleFormControl = new UntypedFormControl();
  isTitleCompletionInProgress = false;

  topicConfig: QueryTopicItem | ConceptTopicItem;
  topicConfigQuery;
  canEditConfig = false;
  editConfigButtonTooltip;
  currentManualTopics = [];

  deletePressed: boolean;
  sentimentValue = 'neutral';

  public readonly hitsTableConfig = {
    concept: {
      columns: ['phrase', 'sentimentScore'],
    },
    query: {
      columns: ['mention'],
    },
  };

  constructor(
    private configurationService: ConfigurationService,
    private industryPackService: IndustryPackService,
    private languageTemplateService: LanguageTemplateService,
    private authService: AuthService,
    private reportService: ReportService,
    private groupService: GroupService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  public topicTypeHasHits(topic: any) {
    return Object.getOwnPropertyNames(this.hitsTableConfig).indexOf(topic.type) > -1;
  }

  public getTopicHits(topic: any) {
    if (topic.type === 'concept') {
      return topic.phrases;
    } else if (topic.type === 'query') {
      return topic.mentions;
    } else {
      return [];
    }
  }

  async ngOnInit() {
    this.currentManualTopics = this.document()
      .analyses[0].topics.filter((t) => t.type === 'manual')
      .map((t) => t.title);

    if (this.topicToEdit()) {
      this.topic = { ...this.topicToEdit() };
      this.sentimentValue = SentimentUtils.getSentimentValueFromRange(
        this.topic.sentimentScore,
        this.options().sentimentRanges.feature,
        this.options().sentimentScale
      );
      this.titleFormControl.setValue(this.topic.title);
      try {
        let apiService: any = this.configurationService;
        if (this.analysisConfigId()?.startsWith('ip:')) {
          apiService = this.industryPackService;
          this.editConfigButtonTooltip = translate(
            'documents.topic-editor.can-not-edit-industry-pack'
          );
        } else if (this.analysisConfigId()?.startsWith('lt:')) {
          apiService = this.languageTemplateService;
          this.editConfigButtonTooltip = translate(
            'documents.topic-editor.can-not-edit-language-template'
          );
        } else if (this.analysisConfigId()?.startsWith('cr:')) {
          // Sorry for this, there's no way to check if the underlying config
          // is an IP or an LT or a normal config...
          try {
            await this.industryPackService.getIndustryPack(this.configId());
            apiService = this.industryPackService;
            this.editConfigButtonTooltip = translate(
              'documents.topic-editor.can-not-edit-route-industry-pack'
            );
          } catch {
            try {
              await this.languageTemplateService.getLanguageTemplate(this.configId());
              apiService = this.languageTemplateService;
              this.editConfigButtonTooltip = translate(
                'documents.topic-editor.can-not-edit-route-language-template'
              );
            } catch {
              this.canEditConfig = true;
            }
          }
        }

        switch (this.topicToEdit().type) {
          case 'concept':
            this.topicConfig = await apiService.getConceptTopic(
              this.configId(),
              this.topicToEdit().id
            );
            break;
          case 'query':
            this.topicConfig = await apiService.getQueryTopic(
              this.configId(),
              this.topicToEdit().id
            );
            break;
          case 'manual':
            this.initTitleAutocomplete();
            break;
          default:
            break;
        }
      } finally {
        if (
          !this.analysisConfigId()?.startsWith('ip:') &&
          !this.analysisConfigId()?.startsWith('lt:') &&
          !this.analysisConfigId()?.startsWith('cr:')
        ) {
          const session = await this.authService.getSession();
          this.canEditConfig = this.authService.canEditConfiguration(session, this.configId());
          if (!this.canEditConfig) {
            this.editConfigButtonTooltip = translate('documents.topic-editor.no-permission');
          }
        }

        this.cdr.markForCheck();
      }
    } else {
      this.topicToEdit.set({});
      this.initTitleAutocomplete();
    }
  }

  hasChanges(): boolean {
    return (
      (this.topic.type === 'manual' &&
        (this.topic.type !== this.topicToEdit().type ||
          this.topic.title !== this.topicToEdit().title)) ||
      (this.topicToEdit() &&
        Utils.roundNearest(this.topic.sentimentScore) !==
          Utils.roundNearest(this.topicToEdit().sentimentScore)) ||
      !this.topicShouldHit
    );
  }

  formValid(): boolean {
    return (
      this.topic.title &&
      (!this.currentManualTopics.includes(this.topic.title) ||
        this.topic.title === this.topicToEdit().title)
    );
  }

  initTitleAutocomplete() {
    this.autocompleteResult = this.titleFormControl.valueChanges.pipe(
      tap((val) => (this.topic.title = val)),
      startWith([]),
      debounceTime(300),
      tap(() => (this.isTitleCompletionInProgress = true)),
      switchMap(() =>
        this.reportService.getReport(
          this.options().collectionName,
          undefined,
          'analyticItems',
          {
            analysisName: this.document().analyses[0].analysisName,
            featureName: 'topics',
            featurePrefix: this.titleFormControl.value,
          },
          'json',
          null,
          null,
          null,
          this.options().projectId
        )
      ),
      map((response: Report) => Object.getOwnPropertyNames(response.items.count)),
      map((items) =>
        items.map((i) => ({ title: i, disabled: this.currentManualTopics.includes(i) }))
      ),
      tap(() => (this.isTitleCompletionInProgress = false))
    );
  }

  autocompleteOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.topic.title = event.option.viewValue;
  }

  onSentimentChanged($event) {
    this.topic.sentimentScore = $event.score;
    this.topic.sentimentPolarity = $event.value;
  }

  cancelTopic() {
    if (this.deletePressed) {
      this.deletePressed = false;
    } else {
      this.cancel.emit();
    }
  }

  deleteTopic() {
    if (this.deletePressed) {
      this.delete.emit({ ...this.topic, comment: this.comment });
    } else {
      this.deletePressed = true;
    }
  }

  saveTopic() {
    if (this.topicShouldHit) {
      this.save.emit({ ...this.topic, comment: this.comment });
    } else {
      this.delete.emit({ ...this.topic, comment: this.comment });
    }
  }

  editConfig() {
    const queryParams = {} as any;
    let fragment;

    if (this.topic.type === 'auto') {
      queryParams.section = 'topics-section';
      fragment = 'settings';
    } else if (this.topic.type === 'concept') {
      if (this.topic.id) {
        queryParams.itemId = this.topic.id;
        queryParams.itemName = this.topic.title;
      }
      fragment = 'concept_topics';
    } else if (this.topic.type === 'query') {
      if (this.topic.id) {
        queryParams.itemId = this.topic.id;
        queryParams.itemName = this.topic.title;
      }
      fragment = 'queries';
    } else {
      fragment = this.topic.type;
    }

    const configsUrl = this.createUrlTree('configurations', this.configId(), {
      queryParams,
      fragment,
    });

    window.open(this.router.serializeUrl(configsUrl));
  }

  editBlacklist() {
    const configsUrl = this.createUrlTree('configurations', this.configId(), {
      queryParams: { itemName: this.topic.title },
      fragment: 'blacklist',
    });

    window.open(this.router.serializeUrl(configsUrl));
  }

  createUrlTree(routeSegment, id, params?) {
    return this.router.createUrlTree(
      ['groups', this.groupService.getSelectedGroupId(true), routeSegment, id],
      params
    );
  }
}
